import React, { useState } from 'react';
import { Button, Grid, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { AccountData, OrderPageTypes, VoucherOut, VoucherType } from '../../../../../api';
import { useAuth } from '../../../../../session/InternalAuthProvider';
import { MANAGEMENT_COMMENT_FIELD_ID } from '../../../../../common/OrderUtils';
import { getEffectivePageLocationId } from '../../../../../common/filter/locationGrouping';
import { useLanguage } from '../../../../../common/GeneralUtils';
import { resourceShortName } from '../../../../../utils/maskUtils';
import { UsersMultiSelect } from '../../../../components/UsersMultiSelect';
import { useCalendar } from '../../../../CalendarContext/CalendarContext';
import { EditOrderFormValues } from '../../EditOrderTypes';
// @ts-ignore
import { useEditOrder } from '../../EditOrderContext';
import { PaymentLinksFields } from '../../components/paymentLinksPanel/PaymentLinksFields';
import { PriceBox } from '../../components/experienceDetailPanel/PriceBox';
import { ResourceSelectionPanel } from '../../components/resourceSelectionPanel/ResourceSelectionPanel';
import { PaymentReceipt } from '../components/PaymentReceipt';

type ExtrasStepProps = {
  voucherToRedeem?: VoucherOut;
  showPaymentLinks: boolean;
  // Set after a failed Create: the receipt flags a missing payment link amount.
  showLinkAmountError: boolean;
};

export const ExtrasStep = ({ voucherToRedeem, showPaymentLinks, showLinkAmountError }: ExtrasStepProps) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { users } = useCalendar();
  const { values, setFieldValue } = useFormikContext<EditOrderFormValues>();
  // @ts-ignore
  const { editOrderState } = useEditOrder();

  const isEventOrCourse = [OrderPageTypes.EVENT, OrderPageTypes.COURSE].includes(
    editOrderState.orderPage.order_page_type,
  );
  const [isPickingTables, setIsPickingTables] = useState(false);

  const schedulerConfigs = account.shared_resources?.scheduler_configs ?? [];
  const orderPageId = editOrderState.orderPage?.id;
  const { persons } = editOrderState.details;
  const showTables = !isEventOrCourse && schedulerConfigs.length > 0 && !!orderPageId && !!persons;
  const editedDuration = values.privateOrderEndTimeSeconds - values.timeSeconds;
  const durationSeconds = editedDuration > 0 ? editedDuration : editOrderState.orderPage?.duration;
  const chosenTableNames = values.selectedHolds.flatMap((hold) =>
    hold.resource_indexes.map((index) =>
      resourceShortName(
        index,
        schedulerConfigs.find((config) => config.id === hold.scheduler_config_id),
        language,
      ),
    ),
  );
  const monetaryVoucherToRedeem = voucherToRedeem?.type === VoucherType.MONETARY ? voucherToRedeem : undefined;
  const voucherRemainderAmount = monetaryVoucherToRedeem
    ? Math.max(0, editOrderState.details.price - (monetaryVoucherToRedeem.balance ?? 0))
    : 0;

  return (
    <Grid container flexDirection="column" gap={2}>
      <Typography variant="caption" color="text.secondary">
        {t('calendar.create_order.step3Hint')}
      </Typography>
      <TextField
        id={MANAGEMENT_COMMENT_FIELD_ID}
        multiline
        label={t('calendar.edit_event.comments')}
        type="text"
        value={values.comment}
        onChange={(e) => setFieldValue('comment', e.target.value)}
      />
      {!isEventOrCourse && (
        <UsersMultiSelect
          users={users}
          selectedUsersIds={values.selectedUsersIds}
          setSelectedUsersIds={(ids) => setFieldValue('selectedUsersIds', ids)}
          locationId={getEffectivePageLocationId(editOrderState.orderPage?.location_id, account)}
        />
      )}
      {showTables && (
        <Grid container flexDirection="column" gap={1}>
          <Grid container alignItems="center" justifyContent="space-between" flexWrap="nowrap" gap={1}>
            <Typography variant="body1" noWrap sx={{ minWidth: 0 }}>
              {chosenTableNames.length > 0 ? chosenTableNames.join(', ') : t('calendar.create_order.tablesAutomatic')}
            </Typography>
            <Button variant="text" onClick={() => setIsPickingTables((isPicking) => !isPicking)}>
              {t('calendar.create_order.pickTables')}
            </Button>
          </Grid>
          {isPickingTables && (
            <ResourceSelectionPanel
              orderPageId={orderPageId}
              persons={persons}
              selectedHolds={values.selectedHolds}
              setSelectedHolds={(holds) => setFieldValue('selectedHolds', holds)}
              date={values.date}
              startSeconds={values.timeSeconds}
              durationSeconds={durationSeconds}
              excludeOrderId={undefined}
              scheduledIndexes={[]}
            />
          )}
        </Grid>
      )}
      {voucherToRedeem ? (
        <>
          {showPaymentLinks && <PaymentLinksFields voucherRemainderAmount={voucherRemainderAmount} />}
          <PriceBox />
        </>
      ) : (
        <PaymentReceipt showLinkCheckbox={showPaymentLinks} showLinkAmountError={showLinkAmountError} />
      )}
    </Grid>
  );
};
